import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { PublicApiError } from "@/api/public-fetch";
import { fetchAssessment, submitAssessment } from "../api/client";

import { AssessmentIntro } from "./AssessmentIntro";
import { AssessmentQuiz } from "./AssessmentQuiz";
import { AssessmentResults } from "./AssessmentResults";

type LoadPhase = "intro" | "quiz";

export function AssessmentPage() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<LoadPhase>("intro");
  // Preserved across a failed submission so we can retry without re-asking.
  const [pendingAnswers, setPendingAnswers] = useState<number[] | null>(null);

  const query = useQuery({
    queryKey: ["assessment", token],
    queryFn: () => fetchAssessment(token!),
    enabled: !!token,
    retry: false,
  });

  const submit = useMutation({
    mutationFn: (answers: number[]) => submitAssessment(token!, answers),
    onSuccess: (data) => {
      queryClient.setQueryData(["assessment", token], data);
      setPendingAnswers(null);
    },
  });

  const handleComplete = (answers: number[]) => {
    setPendingAnswers(answers);
    submit.mutate(answers);
  };

  const retrySubmit = () => {
    if (pendingAnswers) submit.mutate(pendingAnswers);
  };

  if (query.isLoading) {
    return <CenteredMessage text="Loading…" />;
  }
  if (query.error) {
    const status = query.error instanceof PublicApiError ? query.error.status : null;
    if (status === 410) {
      return <CenteredMessage text="This link has expired. Text Wren for a fresh one." />;
    }
    if (status === 404) {
      return <CenteredMessage text="Hmm, this link isn't working. Text Wren for a fresh one." />;
    }
    return <CenteredMessage text="Something glitched on our end. Try again in a moment?" />;
  }

  const data = query.data!;

  if (data.state === "intro") {
    if (phase === "intro") {
      return <AssessmentIntro partnerName={data.partner_name} onStart={() => setPhase("quiz")} />;
    }
    return (
      <div>
        <AssessmentQuiz questions={data.questions!} onComplete={handleComplete} />
        {submit.isPending && <OverlayBanner text="Submitting…" role="status" />}
        {submit.isError && (
          <OverlayBanner
            text="Couldn't send that off. Your answers are safe. Tap retry."
            actionLabel="Retry"
            onAction={retrySubmit}
            role="alert"
          />
        )}
      </div>
    );
  }

  if (data.state === "solo_done_waiting") {
    return (
      <AssessmentResults
        youScores={data.you_scores!}
        languages={data.languages!}
        languageOrder={data.language_order!}
        partnerName={data.partner_name}
      />
    );
  }

  if (data.state === "both_done") {
    return (
      <AssessmentResults
        youScores={data.you_scores!}
        partnerScores={data.partner_scores!}
        languages={data.languages!}
        languageOrder={data.language_order!}
        partnerName={data.partner_name}
        insights={data.insights!}
      />
    );
  }

  return <CenteredMessage text="Unknown state." />;
}

function CenteredMessage({ text }: { text: string }) {
  return (
    <div className="mx-auto max-w-md px-6 py-24 text-center text-[1rem] text-[color:var(--color-wren-supporting)]">
      {text}
    </div>
  );
}

function OverlayBanner({
  text,
  actionLabel,
  onAction,
  role = "status",
}: {
  text: string;
  actionLabel?: string;
  onAction?: () => void;
  role?: "status" | "alert";
}) {
  return (
    <div
      role={role}
      aria-live={role === "alert" ? "assertive" : "polite"}
      className="fixed inset-x-0 bottom-0 z-10 flex justify-center p-4"
    >
      <div
        className="flex max-w-md items-center gap-4 bg-[color:var(--color-wren-green)] px-6 py-4 text-[0.875rem] text-white"
        style={{
          borderRadius: "var(--radius-wren-card)",
          boxShadow: "var(--shadow-wren-button-inset)",
        }}
      >
        <span className="flex-1">{text}</span>
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            className="border border-white/60 bg-transparent px-4 py-2 text-[0.8125rem] font-semibold text-white transition-colors duration-150 hover:bg-white/10 active:bg-white/20"
            style={{ borderRadius: "var(--radius-wren-button-secondary)" }}
          >
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}
